import { useEffect } from 'react';
import useMarketo from '@shared/useMarketo';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useBreadcrumbs } from '@components/shared/withBreadcrumbs';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
import actions from '@store/actions/buildingPage';
import saveActions from '@store/actions/saved';
import routes from '@root/routes';
import { BuildingListing, StoreState } from '@root/types';
import {
  getBuildingBySlug,
  hasCurrentUser as hasCurrentUserSelector,
  isBuildingSaved,
} from '@store/selectors';
import Layout from './Layout';

const mapState = (
  state: StoreState,
  ownProps: { match: { params: { buildingSlug: string } } },
) => ({
  building: getBuildingBySlug(state, ownProps.match.params.buildingSlug),
  hasCurrentUser: hasCurrentUserSelector(state),
  isSaved: isBuildingSaved(state, ownProps.match.params.buildingSlug, null),
});
const mapDispatch = {
  buildingPageLoad: (slug: string, isPreview: boolean) => actions.pageLoad({ slug, isPreview }),
  searchBreadcrumbClicked: () => actions.searchBreadcrumbClicked(),
  contactClicked: () => actions.buildingContactClicked(),
  listingClicked: (listing: BuildingListing, tab: string) =>
    actions.buildingToListingClicked(listing, tab),
  saveBuilding: saveActions.saveBuilding,
  unsaveBuilding: saveActions.unsaveBuilding,
  fetchSaved: saveActions.fetchSavedBuildings,
};

const connector = connect(mapState, mapDispatch);
export type ReduxProps = ConnectedProps<typeof connector>;

export interface Props {
  isPreview?: boolean;
}

const RawBuilding = ({
  building,
  buildingPageLoad,
  searchBreadcrumbClicked,
  contactClicked,
  listingClicked,
  hasCurrentUser,
  saveBuilding,
  unsaveBuilding,
  fetchSaved,
  isSaved,
  isPreview = false,
}: Props & ReduxProps) => {
  const flags = useFlags();
  const { buildingSlug } = useParams<{ buildingSlug: string }>();
  useEffect(() => {
    buildingPageLoad(buildingSlug, isPreview);
    if (hasCurrentUser) {
      fetchSaved();
    }
  }, [buildingSlug]);

  const { setBreadcrumbs } = useBreadcrumbs();

  useMarketo();

  useEffect(() => {
    if (!building || !building?.marketName) return;
    setBreadcrumbs([
      {
        content: building.marketName,
        link: flags['new-search-redirect']
          ? routes.search(building.marketSlug!)
          : routes.listingSearchForMarket(building.marketShapeSlug),
        onClick: searchBreadcrumbClicked,
      },
      building.primaryName,
    ]);
  }, [building]);

  return building ? (
    <Layout
      building={building}
      hasCurrentUser={hasCurrentUser}
      listingClicked={listingClicked}
      contactClicked={contactClicked}
      isSaved={isSaved}
      isPreview={isPreview}
      onSave={saveBuilding}
      onUnsave={unsaveBuilding}
    />
  ) : null;
};

const EnhancedBuilding = connector(RawBuilding);

export { RawBuilding };
export default EnhancedBuilding;
